/**
 * Project Overview 2 — a standalone, Doserne-owned collaborative grid.
 * A local copy of the team's Smartsheet "Project Overview": instant updates,
 * manual per-row colours (toolbar palette), drag-to-reorder, live presence.
 * Talks ONLY to /api/po2 — no external Smartsheet.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, GripVertical, PaintBucket, RefreshCw, Loader2,
  ChevronLeft, Check, X, Circle, Type, ArrowUp, ArrowDown, Eraser,
  Calendar, CalendarDays, ChevronsUp, ChevronsDown, Download, Menu,
  History,
} from "lucide-react";
import { authHeader } from "./capacity/constants";
import DoserneShell from "@/pages/doserne/DoserneShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/po2`;

const COLS_DEFAULT = [
  { key: "done",          label: "✓",             w: 44,  type: "check" },
  { key: "week_number",   label: "Week Number",   w: 120, type: "text" },
  { key: "task_name",     label: "Task Name",     w: 250, type: "text" },
  { key: "start_date",    label: "Start",         w: 104, type: "date" },
  { key: "finish_date",   label: "Finish",        w: 104, type: "date" },
  { key: "status",        label: "Status",        w: 150, type: "status" },
  { key: "comments",      label: "Comments",      w: 380, type: "text" },
  { key: "assigned_team", label: "Assigned team", w: 130, type: "text" },
  { key: "created",       label: "Created",       w: 92,  type: "readonly" },
  { key: "created_by",    label: "Created By",    w: 170, type: "readonly" },
];

const COL_MIN_W = 44;
const COL_MAX_W = 900;
const COL_WIDTHS_STORAGE_KEY = "po2_col_widths_v1";
const TOTAL_W_DEFAULT = COLS_DEFAULT.reduce((s, c) => s + c.w, 0) + 44; // + gutter

// Full preset palette (Smartsheet-style light fills) + clear + custom hex.
const PALETTE = [
  "#fecaca", "#fca5a5", "#fdba74", "#fde68a", "#fef08a", "#d9f99d",
  "#bef264", "#86efac", "#6ee7b7", "#a7f3d0", "#99f6e4", "#67e8f9",
  "#bae6fd", "#93c5fd", "#bfdbfe", "#c4b5fd", "#e9d5ff", "#f0abfc",
  "#f9a8d4", "#fbcfe8", "#e5e7eb", "#d6d3d1",
];

// Per-row text size — Smartsheet-style numeric picker (px). Default: 14.
const TEXT_SIZES = [8, 9, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36];
const DEFAULT_SIZE = 12;
const sizePx = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_SIZE;
};

// Norwegian month names + ISO week helpers (for week/month divider rows).
const MONTHS_NO = ["Januar","Februar","Mars","April","Mai","Juni","Juli","August","September","Oktober","November","Desember"];
const WEEK_COLOR = "#bae6fd";
const MONTH_COLOR = "#93c5fd";

function isoWeek(d) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
}

function parseLooseDate(s) {
  if (!s) return null;
  const str = String(s).trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(str);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = /^(\d{1,2})[./\-](\d{1,2})[./\-](\d{2,4})/.exec(str);
  if (m) { let y = +m[3]; if (y < 100) y += 2000; return new Date(y, +m[2] - 1, +m[1]); }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

function pad2(n) { return String(n).padStart(2, "0"); }
function weekLabel(d) {
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  return `Uke ${isoWeek(monday)} (${pad2(monday.getDate())}.${pad2(monday.getMonth() + 1)} - ${pad2(sunday.getDate())}.${pad2(sunday.getMonth() + 1)})`;
}
function monthLabel(d) { return `${MONTHS_NO[d.getMonth()]} ${d.getFullYear()}`; }

function colorForEmail(email = "") {
  let h = 0;
  for (let i = 0; i < email.length; i++) h = (h * 31 + email.charCodeAt(i)) % 360;
  return `hsl(${h} 70% 45%)`;
}
const initials = (s = "") => s.split(/[@.\s]/).filter(Boolean).slice(0, 2).map((x) => x[0]?.toUpperCase()).join("");

function fmtDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString("nb-NO", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function toIsoDate(v) {
  // Accept ISO or dd.mm.yy / dd.mm.yyyy; return yyyy-mm-dd for <input type="date">.
  const d = parseLooseDate(v);
  if (!d) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export default function ProjectOverview2() {
  const [rows, setRows] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [statusStyles, setStatusStyles] = useState([]); // [{name, bg, fg}]
  const [statusEditor, setStatusEditor] = useState(null); // null or array draft
  const [historyFor, setHistoryFor] = useState(null);     // null or { rowId, loading, edits }
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [anchorId, setAnchorId] = useState(null);  // primary row (for shift-range + toolbar status display)
  const selectedId = anchorId;  // kept for legacy readers below; equals the currently-anchored row
  const [editing, setEditing] = useState(null); // { id, key }
  const [draftVal, setDraftVal] = useState("");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [present, setPresent] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, rowId }
  const [calOpen, setCalOpen] = useState(false);
  const [calMonths, setCalMonths] = useState(2);
  const [calBusy, setCalBusy] = useState(false);
  const [flashes, setFlashes] = useState({});  // { rowId: expiresAtMs } — remote edits glow briefly
  const [currentUserEmail, setCurrentUserEmail] = useState("");

  // Column widths — persisted in localStorage, drag-to-resize via the right edge of each header.
  const [cols, setCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COL_WIDTHS_STORAGE_KEY) || "{}");
      return COLS_DEFAULT.map((c) => ({ ...c, w: Math.max(COL_MIN_W, Math.min(COL_MAX_W, saved[c.key] ?? c.w)) }));
    } catch { return COLS_DEFAULT; }
  });
  const totalW = useMemo(() => cols.reduce((s, c) => s + c.w, 0) + 44, [cols]);
  const resizingRef = useRef(null);  // { key, startX, startW }

  const startResize = (e, key) => {
    e.preventDefault();
    e.stopPropagation();
    const c = cols.find((x) => x.key === key);
    if (!c) return;
    resizingRef.current = { key, startX: e.clientX, startW: c.w };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev) => {
      const r = resizingRef.current;
      if (!r) return;
      const dx = ev.clientX - r.startX;
      const nw = Math.max(COL_MIN_W, Math.min(COL_MAX_W, r.startW + dx));
      setCols((prev) => prev.map((x) => (x.key === r.key ? { ...x, w: nw } : x)));
    };
    const onUp = () => {
      resizingRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      // Persist widths.
      setCols((prev) => {
        try {
          const map = Object.fromEntries(prev.map((x) => [x.key, x.w]));
          localStorage.setItem(COL_WIDTHS_STORAGE_KEY, JSON.stringify(map));
        } catch { /* ignore quota */ }
        return prev;
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const editingRef = useRef(null);
  editingRef.current = editing;
  const selectedIdRef = useRef(null);
  selectedIdRef.current = anchorId;
  const selectedIdsRef = useRef(new Set());
  selectedIdsRef.current = selectedIds;
  const pendingRef = useRef(0); // in-flight saves — pause polling so we don't clobber them
  const rowsSnapshotRef = useRef(new Map()); // id → updated_at of last seen state

  // Capture the signed-in user's email once from localStorage so we can hide our own edits from the flash.
  useEffect(() => {
    try {
      const raw = localStorage.getItem("user") || localStorage.getItem("doserne_user");
      if (raw) setCurrentUserEmail(JSON.parse(raw).email || "");
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/rows`, authHeader());
      // Don't clobber a cell the user is editing, nor an in-flight save.
      if (!editingRef.current && pendingRef.current === 0) {
        const incoming = data.rows || [];
        // Detect rows that changed since last snapshot AND were edited by someone else → flash them.
        const prev = rowsSnapshotRef.current;
        const nextFlash = { ...flashesRef.current };
        const now = Date.now();
        for (const r of incoming) {
          const prevUpd = prev.get(r.id);
          if (prevUpd && prevUpd !== r.updated_at && r.last_updated_by && r.last_updated_by !== currentUserEmail) {
            nextFlash[r.id] = now + 8000; // glow for 8s
          }
        }
        rowsSnapshotRef.current = new Map(incoming.map((r) => [r.id, r.updated_at]));
        setFlashes(nextFlash);
        setRows(incoming);
        setStatusOptions(data.status_options || []);
        setStatusStyles(data.status_styles || []);
      }
    } catch (e) {
      if (showSpinner) toast.error(e?.response?.data?.detail || "Kunne ikke laste rader");
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [currentUserEmail]);

  const flashesRef = useRef({});
  flashesRef.current = flashes;

  // Expire flashes every second — removes glow after 8s automatically.
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      setFlashes((prev) => {
        let changed = false;
        const next = {};
        for (const [id, exp] of Object.entries(prev)) {
          if (exp > now) next[id] = exp;
          else changed = true;
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const loadPresence = useCallback(async () => {
    try {
      await axios.post(`${API}/presence`, { cell: null }, authHeader());
      const { data } = await axios.get(`${API}/presence`, authHeader());
      setPresent(data.present || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(true); }, [load]);
  // Instant updates: poll rows every 2s (paused while editing) + presence.
  useEffect(() => {
    const t = setInterval(() => { if (!editingRef.current) load(false); }, 2000);
    const p = setInterval(loadPresence, 7000);
    loadPresence();
    return () => { clearInterval(t); clearInterval(p); };
  }, [load, loadPresence]);

  // Smartsheet-style keyboard: Insert → add row below, Esc → close menus, Ctrl/Cmd+S → force-save open cell.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setCtxMenu(null); setPaletteOpen(false); setSizeOpen(false); return; }
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        if (editingRef.current) {
          commitEditRef.current?.();
          toast.success("Lagret");
        } else {
          toast.message("Ingen celle i redigering");
        }
        return;
      }
      if (editingRef.current) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      // Ctrl/Cmd+A → select all rows
      if ((e.ctrlKey || e.metaKey) && (e.key === "a" || e.key === "A")) {
        e.preventDefault();
        setSelectedIds(new Set(rows.map((r) => r.id)));
        if (rows[0]) setAnchorId(rows[0].id);
        return;
      }
      if (e.key === "Insert") {
        e.preventDefault();
        addRow(selectedIdRef.current, false); // below (or at end if none selected)
        return;
      }
      // Delete key → bulk delete selection
      if (e.key === "Delete" && selectedIdsRef.current.size > 0) {
        e.preventDefault();
        delSelected();
        return;
      }
      // Alt+Arrow → nudge selected group up/down (Smartsheet-style)
      if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown") && selectedIdsRef.current.size > 0) {
        e.preventDefault();
        nudgeSelection(e.key === "ArrowUp" ? "up" : "down");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const patchLocal = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const saveField = useCallback(async (id, key, value) => {
    patchLocal(id, { [key]: value });
    pendingRef.current += 1;
    try {
      await axios.put(`${API}/rows/${id}`, { [key]: value }, authHeader());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lagring feilet");
      load(false);
    } finally {
      pendingRef.current -= 1;
    }
  }, [load]);

  const sendPresence = useCallback(async (cell) => {
    try { await axios.post(`${API}/presence`, { cell }, authHeader()); } catch { /* ignore */ }
  }, []);

  const commitEdit = async () => {
    if (!editing) return;
    const { id, key } = editing;
    const cur = rows.find((r) => r.id === id);
    setEditing(null);
    sendPresence(null); // free the cell so others may delete the row
    if (cur && String(cur[key] ?? "") !== String(draftVal ?? "")) {
      await saveField(id, key, draftVal);
    }
  };
  const commitEditRef = useRef(null);
  commitEditRef.current = commitEdit;

  const startEdit = (row, key) => {
    setAnchorId(row.id);
    setSelectedIds((prev) => prev.has(row.id) ? prev : new Set([row.id]));
    setEditing({ id: row.id, key });
    setDraftVal(row[key] ?? "");
    sendPresence(`${row.id}|${key}`); // tell others we're editing this cell
  };

  // Multi-select: plain click sets anchor+single; Shift extends range from anchor; Ctrl/Cmd toggles.
  const selectRow = useCallback((id, e) => {
    if (e && e.shiftKey && anchorId) {
      const ids = rows.map((r) => r.id);
      const a = ids.indexOf(anchorId), b = ids.indexOf(id);
      if (a >= 0 && b >= 0) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        setSelectedIds(new Set(ids.slice(lo, hi + 1)));
        return;
      }
    }
    if (e && (e.ctrlKey || e.metaKey)) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
      setAnchorId(id);
      return;
    }
    setSelectedIds(new Set([id]));
    setAnchorId(id);
  }, [rows, anchorId]);

  const clearSelection = () => { setSelectedIds(new Set()); setAnchorId(null); };

  const applyColor = async (color) => {
    if (selectedIds.size === 0) { toast.message("Klikk på en celle i raden først"); return; }
    setPaletteOpen(false);
    for (const id of selectedIds) await saveField(id, "row_color", color);
  };

  const applySize = async (px) => {
    if (selectedIds.size === 0) { toast.message("Klikk på en celle i raden først"); return; }
    setSizeOpen(false);
    for (const id of selectedIds) await saveField(id, "text_size", Number(px));
  };

  const addRow = async (anchorRowId, above, extra = {}) => {
    try {
      const { data } = await axios.post(`${API}/rows`, { anchor_id: anchorRowId || null, above: !!above, ...extra }, authHeader());
      await load(false);
      setAnchorId(data.id);
      setSelectedIds(new Set([data.id]));
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke legge til rad"); }
  };

  const insertWeekDivider = (anchorId) => {
    const anchor = rows.find((r) => r.id === anchorId);
    const d = parseLooseDate(anchor?.start_date) || new Date();
    return addRow(anchorId, true, {
      week_number: weekLabel(d),
      is_section: true, bold: true,
      row_color: WEEK_COLOR, text_size: 14,
    });
  };

  const insertMonthDivider = (anchorId) => {
    const anchor = rows.find((r) => r.id === anchorId);
    const d = parseLooseDate(anchor?.start_date) || new Date();
    return addRow(anchorId, true, {
      week_number: monthLabel(d),
      is_section: true, bold: true,
      row_color: MONTH_COLOR, text_size: 16,
    });
  };

  const generateCalendar = async () => {
    const n = Math.max(1, Math.min(12, Number(calMonths) || 2));
    setCalBusy(true);
    try {
      const { data } = await axios.post(`${API}/generate-calendar`, { months: n }, authHeader());
      await load(false);
      setCalOpen(false);
      toast.success(`La til ${data.created} rader (${n} måneder)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kalendergenerering feilet"); }
    finally { setCalBusy(false); }
  };

  const saveStatuses = async () => {
    const cleaned = (statusEditor || [])
      .map((s) => ({ name: (s.name || "").trim(), bg: s.bg || "#f1f5f9", fg: s.fg || "#475569" }))
      .filter((s) => s.name);
    if (!cleaned.length) { toast.error("Minst én status kreves"); return; }
    try {
      const { data } = await axios.put(`${API}/statuses`, { statuses: cleaned }, authHeader());
      setStatusStyles(data.statuses || cleaned);
      setStatusOptions((data.statuses || cleaned).map((s) => s.name));
      setStatusEditor(null);
      toast.success("Statuser lagret");
    } catch (e) { toast.error(e?.response?.data?.detail || "Lagring feilet"); }
  };

  const openHistory = async (rowId) => {
    setHistoryFor({ rowId, loading: true, edits: [] });
    try {
      const { data } = await axios.get(`${API}/rows/${rowId}/edits`, authHeader());
      setHistoryFor({ rowId, loading: false, edits: data.edits || [] });
    } catch (e) {
      setHistoryFor({ rowId, loading: false, edits: [] });
      toast.error("Kunne ikke laste endringshistorikk");
    }
  };

  // Soft lock: refuse row-level destructive ops when another admin is editing a cell in the row.
  const rowBusyBy = (id) => {
    const p = present.find((p) => typeof p.cell === "string" && p.cell.startsWith(`${id}|`));
    return p ? (p.name || p.email) : null;
  };

  const delRow = async (id) => {
    const by = rowBusyBy(id);
    if (by) { toast.error(`${by} redigerer denne raden akkurat nå — venter til de er ferdige`); return; }
    patchLocal(id, {});
    setRows((rs) => rs.filter((r) => r.id !== id));
    setSelectedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    if (anchorId === id) setAnchorId(null);
    try { await axios.delete(`${API}/rows/${id}`, authHeader()); }
    catch (e) { toast.error("Sletting feilet"); load(false); }
  };

  // Bulk delete: all selected rows, respecting per-row soft lock.
  const delSelected = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const blocked = ids.map((id) => ({ id, by: rowBusyBy(id) })).filter((x) => x.by);
    if (blocked.length) {
      toast.error(`${blocked.length} rad(er) blokkert — ${blocked[0].by} redigerer`);
      return;
    }
    setRows((rs) => rs.filter((r) => !selectedIds.has(r.id)));
    clearSelection();
    for (const id of ids) {
      try { await axios.delete(`${API}/rows/${id}`, authHeader()); } catch { /* ignore */ }
    }
    load(false);
  };

  // Smartsheet-style "nudge group" — move the pivot (adjacent non-selected row)
  // AROUND the whole selected block, in a single move call. Keeps the group tight.
  const nudgeSelection = async (dir) => {
    if (selectedIds.size === 0) return;
    const ids = rows.map((r) => r.id);
    const selIdx = ids.map((id, i) => (selectedIds.has(id) ? i : -1)).filter((i) => i >= 0);
    if (selIdx.length === 0) return;
    const first = selIdx[0], last = selIdx[selIdx.length - 1];
    try {
      if (dir === "up") {
        if (first === 0) return;
        const pivot = rows[first - 1];
        const anchor = rows[last];
        // Place pivot just below anchor → group shifts up by 1
        await axios.post(`${API}/rows/${pivot.id}/move`, { anchor_id: anchor.id, above: false }, authHeader());
      } else {
        if (last === rows.length - 1) return;
        const pivot = rows[last + 1];
        const anchor = rows[first];
        await axios.post(`${API}/rows/${pivot.id}/move`, { anchor_id: anchor.id, above: true }, authHeader());
      }
      await load(false);
    } catch (e) { toast.error("Flytting feilet"); }
  };

  const clearRow = async (id) => {
    const by = rowBusyBy(id);
    if (by) { toast.error(`${by} redigerer denne raden — venter til de er ferdige`); return; }
    const empty = {
      teams: "", week_number: "", task_name: "",
      start_date: "", finish_date: "", status: "",
      comments: "", assigned_team: "", done: false,
    };
    patchLocal(id, empty);
    pendingRef.current += 1;
    try { await axios.put(`${API}/rows/${id}`, empty, authHeader()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Tømming feilet"); load(false); }
    finally { pendingRef.current -= 1; }
  };

  const openCtxMenu = (e, rowId) => {
    e.preventDefault();
    // If the row is NOT part of the current multi-selection, replace selection with just this row.
    if (!selectedIdsRef.current.has(rowId)) {
      setAnchorId(rowId);
      setSelectedIds(new Set([rowId]));
    }
    // Clamp to viewport so menu never spills off-screen.
    const W = 260, H = 380;
    const x = Math.min(e.clientX, window.innerWidth - W - 8);
    const y = Math.min(e.clientY, window.innerHeight - H - 8);
    setCtxMenu({ x, y, rowId });
  };

  const onDrop = async (targetRow) => {
    const moved = dragId;
    setDragId(null);
    if (!moved || moved === targetRow.id) return;
    // Optimistic reorder: place moved just above target.
    setRows((rs) => {
      const m = rs.find((r) => r.id === moved);
      const without = rs.filter((r) => r.id !== moved);
      const idx = without.findIndex((r) => r.id === targetRow.id);
      without.splice(idx, 0, m);
      return [...without];
    });
    try { await axios.post(`${API}/rows/${moved}/move`, { anchor_id: targetRow.id, above: true }, authHeader()); }
    catch { toast.error("Flytting feilet"); load(false); }
  };

  const selectedRow = useMemo(() => rows.find((r) => r.id === selectedId), [rows, selectedId]);
  const statusMap = useMemo(() => {
    const m = {};
    for (const s of statusStyles || []) m[s.name] = { bg: s.bg, fg: s.fg };
    return m;
  }, [statusStyles]);

  return (
    <DoserneShell wide>
      <div className="theme-light -mx-4 sm:-mx-0 bg-[#f1f5f9] text-slate-800 min-h-[70vh]">
        {/* Toolbar */}
        <div className="sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 px-4 py-2.5 flex-wrap mx-auto" style={{ maxWidth: totalW }}>
            <span className="text-lg font-black tracking-tight mr-2">Project Overview</span>

          <button type="button" onClick={() => addRow(null, false)} data-testid="po2-add-row"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600 text-white text-xs font-black hover:bg-cyan-700">
            <Plus className="w-4 h-4" /> Ny rad
          </button>

          {/* Fill colour (palette) */}
          <div className="relative">
            <button type="button" onClick={() => setPaletteOpen((v) => !v)} data-testid="po2-color-btn"
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-black ${selectedId ? "bg-white border-slate-200 text-slate-700 hover:bg-slate-50" : "bg-slate-100 border-slate-200 text-slate-400"}`}>
              <PaintBucket className="w-4 h-4" /> Farge
              {selectedRow?.row_color ? (
                <span className="w-3.5 h-3.5 rounded-sm border border-slate-300" style={{ backgroundColor: selectedRow.row_color }} />
              ) : null}
            </button>
            {paletteOpen && (
              <div className="absolute z-50 mt-2 left-0 w-60 rounded-xl border border-slate-200 bg-white shadow-2xl p-3" data-testid="po2-palette">
                {!selectedId && <p className="text-[11px] text-slate-400 mb-2">Klikk på en celle i raden først.</p>}
                <div className="grid grid-cols-6 gap-1.5">
                  {PALETTE.map((c) => (
                    <button key={c} type="button" onClick={() => applyColor(c)} title={c}
                            data-testid={`po2-swatch-${c}`}
                            className="w-7 h-7 rounded-md border border-slate-200 hover:ring-2 hover:ring-cyan-400 transition"
                            style={{ backgroundColor: c }} />
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                  <button type="button" onClick={() => applyColor("")} data-testid="po2-swatch-clear"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-[11px] font-bold text-slate-600 hover:bg-slate-50">
                    <X className="w-3.5 h-3.5" /> Fjern farge
                  </button>
                  <label className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-600 cursor-pointer ml-auto">
                    Egen
                    <input type="color" onChange={(e) => applyColor(e.target.value)} data-testid="po2-custom-color"
                           className="w-7 h-7 rounded cursor-pointer border border-slate-200 bg-white p-0" />
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Text size (Smartsheet-style numeric) */}
          <div className="relative">
            <button type="button" onClick={() => setSizeOpen((v) => !v)} data-testid="po2-size-btn"
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-black ${selectedId ? "bg-white border-slate-200 text-slate-700 hover:bg-slate-50" : "bg-slate-100 border-slate-200 text-slate-400"}`}>
              <Type className="w-4 h-4" />
              <span className="tabular-nums">{sizePx(selectedRow?.text_size)}</span>
              <svg width="8" height="8" viewBox="0 0 10 10" className="opacity-60"><path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth="1.6" fill="none" /></svg>
            </button>
            {sizeOpen && (
              <div className="absolute z-50 mt-2 left-0 w-20 rounded-lg border border-slate-200 bg-white shadow-2xl py-1 max-h-72 overflow-y-auto" data-testid="po2-size-menu">
                {TEXT_SIZES.map((px) => {
                  const active = sizePx(selectedRow?.text_size) === px;
                  return (
                    <button key={px} type="button" onClick={() => applySize(px)}
                            data-testid={`po2-size-${px}`}
                            className={`w-full text-left px-3 py-1 text-[12px] tabular-nums ${active ? "bg-cyan-50 text-cyan-700 font-black" : "text-slate-700 hover:bg-slate-50"}`}>
                      {px}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button type="button" onClick={() => load(true)} data-testid="po2-refresh"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-black hover:bg-slate-50">
            <RefreshCw className="w-4 h-4" /> Oppdater
          </button>

          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 ml-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Live
          </span>

          {selectedIds.size > 0 ? (
            <div className="inline-flex items-center gap-1 text-[11px] font-bold text-cyan-700 ml-1 px-2 py-1 rounded-lg bg-cyan-50 border border-cyan-200">
              <Check className="w-3.5 h-3.5" /> {selectedIds.size} valgt
              <button type="button" title="Flytt opp (Alt + ↑)" onClick={() => nudgeSelection("up")}
                      data-testid="po2-move-up"
                      className="ml-1 p-0.5 rounded hover:bg-cyan-100 text-cyan-700">
                <ChevronsUp className="w-3.5 h-3.5" />
              </button>
              <button type="button" title="Flytt ned (Alt + ↓)" onClick={() => nudgeSelection("down")}
                      data-testid="po2-move-down"
                      className="p-0.5 rounded hover:bg-cyan-100 text-cyan-700">
                <ChevronsDown className="w-3.5 h-3.5" />
              </button>
              <button type="button" title="Slett valgte (Delete)" onClick={delSelected}
                      data-testid="po2-del-selected"
                      className="p-0.5 rounded hover:bg-red-100 text-red-600">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <button type="button" title="Avmarkér" onClick={clearSelection}
                      data-testid="po2-clear-selection"
                      className="p-0.5 rounded hover:bg-slate-200 text-slate-500">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <span className="text-[11px] font-semibold text-slate-400 ml-1 hidden md:inline">
              Tips: Shift/Ctrl-klikk for flere rader
            </span>
          )}

          {/* Presence */}
          <div className="flex items-center gap-1 ml-auto">
            {present.length > 0 && (
              <span className="text-[11px] font-black text-slate-600 mr-1 px-2 py-0.5 rounded-full bg-slate-100" data-testid="po2-presence-count">
                {present.length + 1} online
              </span>
            )}
            {present.map((p) => (
              <span key={p.email} title={p.name || p.email} data-testid={`po2-presence-${p.email}`}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black text-white border-2 border-white -ml-2 shadow"
                    style={{ backgroundColor: colorForEmail(p.email) }}>
                {initials(p.name || p.email)}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="overflow-x-auto">
        <table className="border-collapse text-[13px] mx-auto" style={{ tableLayout: "fixed", width: totalW }}>
          <thead>
            <tr className="bg-slate-100 text-slate-500 text-[10px] uppercase tracking-widest">
              <th style={{ width: 44 }} className="px-1 py-2 border-b border-slate-200" />
              {cols.map((c) => (
                <th key={c.key} style={{ width: c.w, position: "relative" }}
                    onDoubleClick={c.key === "status" ? () => setStatusEditor(statusStyles.map((s) => ({ ...s }))) : undefined}
                    data-testid={c.key === "status" ? "po2-status-header" : undefined}
                    title={c.key === "status" ? "Dobbeltklikk for å redigere statuser" : undefined}
                    className={`px-2 py-2 border-b border-r border-slate-200 text-left font-black ${c.key === "status" ? "cursor-pointer hover:bg-slate-200" : ""}`}>
                  {c.label}
                  {/* Resize handle on the right edge */}
                  <span onMouseDown={(e) => startResize(e, c.key)}
                        data-testid={`po2-resize-${c.key}`}
                        title="Dra for å endre bredden"
                        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-cyan-400 active:bg-cyan-500"
                        style={{ userSelect: "none" }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={cols.length + 1} className="text-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={cols.length + 1} className="text-center py-16 text-slate-400 text-sm">Ingen rader ennå. Trykk «Ny rad».</td></tr>
            ) : rows.map((row, idx) => {
              const sel = selectedIds.has(row.id);
              const isAnchor = anchorId === row.id;
              const st = statusMap[row.status];
              const rowBg = row.row_color || (st ? st.bg : undefined);
              const fs = sizePx(row.text_size);
              const isFlashing = !!flashes[row.id];
              return (
                <tr key={row.id} data-testid={`po2-row-${row.id}`}
                    draggable
                    onClick={(e) => selectRow(row.id, e)}
                    onContextMenu={(e) => openCtxMenu(e, row.id)}
                    onDragStart={() => setDragId(row.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(row)}
                    className={`group cursor-default ${isAnchor ? "ring-2 ring-cyan-500 ring-inset" : ""} ${isFlashing ? "po2-flash" : ""}`}
                    title={isFlashing ? `Oppdatert av ${row.last_updated_by || "annen bruker"}` : undefined}
                    style={{ backgroundColor: rowBg }}>
                  {/* Gutter: click toggles selection (Shift/Ctrl aware for ranges) */}
                  <td onClick={(e) => { e.stopPropagation(); selectRow(row.id, e); }}
                      data-testid={`po2-select-${row.id}`}
                      title={sel ? "Shift/Ctrl + klikk for utvalg" : "Klikk for å markere raden (Shift for serie, Ctrl for flere)"}
                      className={`border-b border-slate-200 text-center align-middle relative cursor-pointer ${sel ? "" : "hover:bg-slate-200/60"}`}
                      style={{ width: 44, backgroundColor: sel ? "#0891b2" : rowBg }}>
                    <div className="flex items-center justify-center gap-0.5">
                      <GripVertical className={`w-3 h-3 cursor-grab opacity-0 group-hover:opacity-100 ${sel ? "text-white/80" : "text-slate-300"}`} />
                      <span className={`text-[11px] font-mono w-5 ${sel ? "text-white font-black" : "text-slate-400"}`}>
                        {sel ? "✓" : idx + 1}
                      </span>
                    </div>
                    {isAnchor && selectedIds.size === 1 && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); delRow(row.id); }} data-testid={`po2-del-${row.id}`}
                              className="absolute -right-0 top-1/2 -translate-y-1/2 p-0.5 text-white/80 hover:text-white">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>

                  {cols.map((c) => {
                    const isEditing = editing && editing.id === row.id && editing.key === c.key;
                    const val = row[c.key];

                    if (c.key === "done") {
                      return (
                        <td key={c.key} className="border-b border-r border-slate-200 text-center" style={{ width: c.w }}>
                          <input type="checkbox" checked={!!val} data-testid={`po2-done-${row.id}`}
                                 onChange={(e) => saveField(row.id, "done", e.target.checked)}
                                 className="w-4 h-4 accent-cyan-600 cursor-pointer" />
                        </td>
                      );
                    }
                    if (c.key === "status") {
                      return (
                        <td key={c.key} className="border-b border-r border-slate-200 px-1" style={{ width: c.w }}>
                          <select value={val || ""} data-testid={`po2-status-${row.id}`}
                                  onChange={(e) => saveField(row.id, "status", e.target.value)}
                                  className="w-full px-2 py-1 rounded font-bold border-0 outline-none cursor-pointer"
                                  style={{ fontSize: `${fs}px`, ...(st ? { backgroundColor: "transparent", color: st.fg } : { background: "transparent", color: "#64748b" }) }}>
                            <option value="">—</option>
                            {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                      );
                    }
                    if (c.type === "date") {
                      // Click → native date picker. Save ISO yyyy-mm-dd; display formatted.
                      return (
                        <td key={c.key} className="border-b border-r border-slate-200 p-0 align-middle po2-date-cell" style={{ width: c.w, backgroundColor: "transparent" }}>
                          <input type="date" value={toIsoDate(val)}
                                 onClick={(e) => { if (!selectedIds.has(row.id)) { setAnchorId(row.id); setSelectedIds(new Set([row.id])); } if (e.target.showPicker) try { e.target.showPicker(); } catch { /* older browsers */ } }}
                                 onChange={(e) => saveField(row.id, c.key, e.target.value)}
                                 data-testid={`po2-date-${c.key}-${row.id}`}
                                 style={{ fontSize: `${fs}px` }}
                                 className="w-full px-2 py-1 border-0 outline-none text-slate-800 cursor-pointer hover:bg-black/5" />
                        </td>
                      );
                    }
                    if (c.type === "readonly") {
                      return (
                        <td key={c.key} className="border-b border-r border-slate-200 px-2 py-1 text-slate-500 truncate"
                            style={{ width: c.w, fontSize: `${Math.max(10, fs - 1)}px` }}>
                          {c.key === "created" ? (fmtDate(val) || "—") : (val || "—")}
                        </td>
                      );
                    }
                    // Editable text / date cell
                    const isComments = c.key === "comments";
                    return (
                      <td key={c.key} className="border-b border-r border-slate-200 p-0 align-top relative" style={{ width: c.w }}>
                        {isEditing ? (
                          isComments ? (
                            // Float the editor over the rows below so clicking "Comments" never pushes anything.
                            <textarea
                              autoFocus rows={6}
                              value={draftVal}
                              onChange={(e) => setDraftVal(e.target.value)}
                              onBlur={commitEdit}
                              onKeyDown={(e) => { if (e.key === "Escape") setEditing(null); }}
                              data-testid={`po2-input-${c.key}-${row.id}`}
                              style={{ fontSize: `${fs}px`, minWidth: c.w, width: Math.max(c.w, 420) }}
                              className="absolute left-0 top-0 z-30 px-2 py-1 border-2 border-cyan-500 outline-none text-slate-900 bg-white shadow-xl resize rounded-sm" />
                          ) : (
                            <textarea
                              autoFocus rows={1}
                              value={draftVal}
                              onChange={(e) => setDraftVal(e.target.value)}
                              onBlur={commitEdit}
                              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commitEdit(); } if (e.key === "Escape") setEditing(null); }}
                              data-testid={`po2-input-${c.key}-${row.id}`}
                              style={{ fontSize: `${fs}px` }}
                              className="w-full px-2 py-1 border border-cyan-400 outline-none text-slate-900 bg-white resize-none" />
                          )
                        ) : (
                          <button type="button" onClick={() => startEdit(row, c.key)}
                                  data-testid={`po2-cell-${c.key}-${row.id}`}
                                  style={{ fontSize: `${fs}px` }}
                                  className={`w-full text-left px-2 py-1 min-h-[26px] text-slate-800 whitespace-pre-wrap break-words hover:bg-black/5 ${row.bold ? "font-black" : ""}`}>
                            {val || <span className="text-transparent group-hover:text-slate-300">—</span>}
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* click-away to close palette */}
      {paletteOpen && <div className="fixed inset-0 z-40" onClick={() => setPaletteOpen(false)} />}
      {sizeOpen && <div className="fixed inset-0 z-40" onClick={() => setSizeOpen(false)} />}

      {/* Row right-click context menu (Smartsheet-style) */}
      {ctxMenu && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setCtxMenu(null)} onContextMenu={(e) => { e.preventDefault(); setCtxMenu(null); }} />
          <div data-testid="po2-ctx-menu"
               className="fixed z-[61] w-64 rounded-lg border border-slate-200 bg-white shadow-2xl py-1 text-[12px]"
               style={{ left: ctxMenu.x, top: ctxMenu.y }}>
            <button type="button" onClick={() => { addRow(ctxMenu.rowId, true); setCtxMenu(null); }}
                    data-testid="po2-ctx-insert-above"
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-slate-700 hover:bg-slate-100">
              <ArrowUp className="w-3.5 h-3.5 text-slate-500" /> Sett inn rad over
            </button>
            <button type="button" onClick={() => { addRow(ctxMenu.rowId, false); setCtxMenu(null); }}
                    data-testid="po2-ctx-insert-below"
                    className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-slate-700 hover:bg-slate-100">
              <span className="flex items-center gap-2"><ArrowDown className="w-3.5 h-3.5 text-slate-500" /> Sett inn rad under</span>
              <span className="text-[10px] text-slate-400 font-mono">Insert</span>
            </button>

            <div className="my-1 border-t border-slate-100" />

            <button type="button" onClick={() => { insertWeekDivider(ctxMenu.rowId); setCtxMenu(null); }}
                    data-testid="po2-ctx-insert-week"
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-slate-700 hover:bg-slate-100">
              <Calendar className="w-3.5 h-3.5 text-cyan-600" /> Sett inn uke-skillelinje over
            </button>
            <button type="button" onClick={() => { insertMonthDivider(ctxMenu.rowId); setCtxMenu(null); }}
                    data-testid="po2-ctx-insert-month"
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-slate-700 hover:bg-slate-100">
              <CalendarDays className="w-3.5 h-3.5 text-blue-600" /> Sett inn måneds-skillelinje over
            </button>

            <div className="my-1 border-t border-slate-100" />

            {/* Fill colour palette inline */}
            <div className="px-3 py-1.5">
              <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500 mb-1.5">
                <PaintBucket className="w-3.5 h-3.5" /> Fyllfarge
              </div>
              <div className="grid grid-cols-11 gap-1">
                {PALETTE.map((c) => (
                  <button key={c} type="button" title={c}
                          onClick={() => { saveField(ctxMenu.rowId, "row_color", c); setCtxMenu(null); }}
                          data-testid={`po2-ctx-swatch-${c}`}
                          className="w-4 h-4 rounded-sm border border-slate-200 hover:ring-2 hover:ring-cyan-400"
                          style={{ backgroundColor: c }} />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <button type="button"
                        onClick={() => { saveField(ctxMenu.rowId, "row_color", ""); setCtxMenu(null); }}
                        data-testid="po2-ctx-swatch-clear"
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-slate-200 text-[10px] font-bold text-slate-600 hover:bg-slate-50">
                  <X className="w-3 h-3" /> Fjern
                </button>
                <label className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-600 cursor-pointer ml-auto">
                  Egen
                  <input type="color"
                         onChange={(e) => { saveField(ctxMenu.rowId, "row_color", e.target.value); setCtxMenu(null); }}
                         data-testid="po2-ctx-custom-color"
                         className="w-5 h-5 rounded cursor-pointer border border-slate-200 bg-white p-0" />
                </label>
              </div>
            </div>

            <div className="my-1 border-t border-slate-100" />

            {/* Text size inline */}
            <div className="px-3 py-1.5">
              <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500 mb-1.5">
                <Type className="w-3.5 h-3.5" /> Tekststørrelse
              </div>
              <div className="grid grid-cols-6 gap-1">
                {TEXT_SIZES.map((px) => {
                  const curRow = rows.find((r) => r.id === ctxMenu.rowId);
                  const active = sizePx(curRow?.text_size) === px;
                  return (
                    <button key={px} type="button"
                            onClick={() => { saveField(ctxMenu.rowId, "text_size", Number(px)); setCtxMenu(null); }}
                            data-testid={`po2-ctx-size-${px}`}
                            className={`px-1 py-0.5 rounded text-[11px] tabular-nums border ${active ? "bg-cyan-50 border-cyan-400 text-cyan-700 font-black" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                      {px}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="my-1 border-t border-slate-100" />

            <button type="button" onClick={() => { openHistory(ctxMenu.rowId); setCtxMenu(null); }}
                    data-testid="po2-ctx-history"
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-slate-700 hover:bg-slate-100">
              <History className="w-3.5 h-3.5 text-slate-500" /> Vis endringer
            </button>
            <button type="button" onClick={() => { clearRow(ctxMenu.rowId); setCtxMenu(null); }}
                    data-testid="po2-ctx-clear"
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-slate-700 hover:bg-slate-100">
              <Eraser className="w-3.5 h-3.5 text-slate-500" /> Tøm innhold
            </button>
            <button type="button" onClick={() => { delRow(ctxMenu.rowId); setCtxMenu(null); }}
                    data-testid="po2-ctx-delete"
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-red-600 hover:bg-red-50">
              <Trash2 className="w-3.5 h-3.5" /> Slett rad
            </button>
          </div>
        </>
      )}

      {/* Status editor modal (dobbeltklikk på Status-kolonneoverskrift) */}
      {statusEditor && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/40"
             onClick={() => setStatusEditor(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="po2-status-editor"
               className="w-[480px] max-w-full max-h-[85vh] overflow-y-auto rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 sticky top-0 bg-white">
              <h3 className="text-sm font-black text-slate-800">Rediger statuser</h3>
              <button type="button" onClick={() => setStatusEditor(null)} className="p-1 rounded hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="px-4 pt-3 text-[11px] text-slate-500">Legg til, gi nytt navn, endre farger eller slett. Rekkefølgen her blir rekkefølgen i nedtrekkslisten.</p>
            <div className="px-4 py-3 space-y-2">
              {statusEditor.map((s, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2 border border-slate-200">
                  <span className="px-2 py-1 rounded font-bold text-[11px] whitespace-nowrap"
                        style={{ backgroundColor: s.bg, color: s.fg }}>Aa</span>
                  <input type="text" value={s.name}
                         onChange={(e) => setStatusEditor((arr) => arr.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                         data-testid={`po2-status-name-${i}`}
                         className="flex-1 min-w-0 px-2 py-1 rounded border border-slate-200 text-[12px] text-slate-800 outline-none focus:border-cyan-400" />
                  <label className="flex items-center gap-1 text-[10px] font-bold text-slate-600" title="Bakgrunn">
                    BG
                    <input type="color" value={s.bg || "#f1f5f9"}
                           onChange={(e) => setStatusEditor((arr) => arr.map((x, j) => j === i ? { ...x, bg: e.target.value } : x))}
                           data-testid={`po2-status-bg-${i}`}
                           className="w-6 h-6 rounded cursor-pointer border border-slate-200 bg-white p-0" />
                  </label>
                  <label className="flex items-center gap-1 text-[10px] font-bold text-slate-600" title="Tekst">
                    FG
                    <input type="color" value={s.fg || "#475569"}
                           onChange={(e) => setStatusEditor((arr) => arr.map((x, j) => j === i ? { ...x, fg: e.target.value } : x))}
                           data-testid={`po2-status-fg-${i}`}
                           className="w-6 h-6 rounded cursor-pointer border border-slate-200 bg-white p-0" />
                  </label>
                  <button type="button"
                          onClick={() => setStatusEditor((arr) => arr.filter((_, j) => j !== i))}
                          data-testid={`po2-status-del-${i}`}
                          className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button type="button"
                      onClick={() => setStatusEditor((arr) => [...arr, { name: "Ny status", bg: "#e2e8f0", fg: "#334155" }])}
                      data-testid="po2-status-add"
                      className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-dashed border-slate-300 text-slate-500 text-[12px] font-bold hover:bg-slate-50 hover:text-cyan-700 hover:border-cyan-300">
                <Plus className="w-3.5 h-3.5" /> Legg til status
              </button>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setStatusEditor(null)}
                      data-testid="po2-status-cancel"
                      className="px-3 py-1.5 rounded-lg text-[12px] font-bold text-slate-600 hover:bg-slate-100">Avbryt</button>
              <button type="button" onClick={saveStatuses}
                      data-testid="po2-status-save"
                      className="px-3 py-1.5 rounded-lg bg-cyan-600 text-white text-[12px] font-black hover:bg-cyan-700">Lagre</button>
            </div>
          </div>
        </div>
      )}
      {/* Row edit-history modal */}
      {historyFor && (() => {
        const row = rows.find((r) => r.id === historyFor.rowId);
        const title = row ? (row.task_name || row.week_number || "Rad") : "Rad";
        const colLabel = (k) => (COLS_DEFAULT.find((c) => c.key === k)?.label) || k;
        const fmtAt = (iso) => { try { return new Date(iso).toLocaleString("nb-NO"); } catch { return iso; } };
        return (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/40"
               onClick={() => setHistoryFor(null)}>
            <div onClick={(e) => e.stopPropagation()} data-testid="po2-history-modal"
                 className="w-[640px] max-w-full max-h-[85vh] overflow-y-auto rounded-xl bg-white shadow-2xl">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 sticky top-0 bg-white">
                <h3 className="text-sm font-black text-slate-800 truncate">Endringshistorikk — <span className="text-slate-500 font-bold">{title}</span></h3>
                <button type="button" onClick={() => setHistoryFor(null)} className="p-1 rounded hover:bg-slate-100 text-slate-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="px-4 py-3">
                {historyFor.loading ? (
                  <div className="text-center py-8 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
                ) : historyFor.edits.length === 0 ? (
                  <p className="text-center text-[12px] text-slate-400 py-8">Ingen endringer registrert ennå for denne raden.</p>
                ) : (
                  <table className="w-full text-[12px]">
                    <thead className="text-[10px] uppercase tracking-widest text-slate-400">
                      <tr>
                        <th className="text-left py-1 pr-2">Felt</th>
                        <th className="text-left py-1 pr-2">Ny verdi</th>
                        <th className="text-left py-1 pr-2">Av</th>
                        <th className="text-left py-1">Når</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyFor.edits.map((e, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="py-1.5 pr-2 font-bold text-slate-700 whitespace-nowrap">{colLabel(e.field)}</td>
                          <td className="py-1.5 pr-2 text-slate-800 max-w-[220px] truncate" title={String(e.new ?? "")}>
                            {String(e.new ?? "") || <span className="text-slate-300">(tomt)</span>}
                          </td>
                          <td className="py-1.5 pr-2 text-slate-600 whitespace-nowrap">{e.by_name || e.by_email}</td>
                          <td className="py-1.5 text-slate-500 whitespace-nowrap">{fmtAt(e.at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        );
      })()}
      </div>
    </DoserneShell>
  );
}
